import React, { useState } from 'react';
import { X } from 'lucide-react';

const DuplicarClaseModal = ({ isOpen, onClose, clase, semestres, onDuplicar }) => {
  const [formData, setFormData] = useState({
    nuevoNombre: `${clase?.nombre || clase?.titulo || ''} - Copia`,
    nuevoAnio: new Date().getFullYear() + 2,
    fechaInicio: '',
    semestreId: clase?.semestres?.[0] || '',
    copiarMateriales: true,
    copiarFormularios: true,
    copiarTareas: true
  });
  
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onDuplicar(formData);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <i className="fas fa-copy"></i>
              Duplicar Clase
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Crear una copia de "{clase?.nombre || clase?.titulo}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nombre de la Nueva Clase */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-tag mr-2 text-blue-600"></i>
              Nombre de la Nueva Clase *
            </label>
            <input
              type="text"
              value={formData.nuevoNombre}
              onChange={(e) => setFormData({ ...formData, nuevoNombre: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ej: Homilectica 2027"
              required
            />
          </div>

          {/* Año y Fecha */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-calendar mr-2 text-green-600"></i>
                Año Académico
              </label>
              <input
                type="number"
                value={formData.nuevoAnio}
                onChange={(e) => setFormData({ ...formData, nuevoAnio: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="2024"
                max="2030"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-calendar-day mr-2 text-purple-600"></i>
                Fecha de Inicio (Opcional)
              </label>
              <input
                type="date"
                value={formData.fechaInicio}
                onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Semestre */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-graduation-cap mr-2 text-indigo-600"></i>
              Semestre
            </label>
            <select
              value={formData.semestreId}
              onChange={(e) => setFormData({ ...formData, semestreId: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Seleccionar semestre...</option>
              {semestres.map(sem => (
                <option key={sem.id} value={sem.semester_number || sem.numero}>
                  {sem.name || sem.nombre || `Semestre ${sem.semester_number || sem.numero}`}
                </option>
              ))}
            </select>
          </div>

          {/* Opciones de Copia */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <i className="fas fa-clipboard-check text-blue-600"></i>
              ¿Qué deseas copiar?
            </h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.copiarMateriales}
                  onChange={(e) => setFormData({ ...formData, copiarMateriales: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                    Materiales (PDFs, videos, enlaces)
                  </div>
                  <div className="text-xs text-gray-500">Copia todos los recursos de la clase</div>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.copiarFormularios}
                  onChange={(e) => setFormData({ ...formData, copiarFormularios: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                    Formularios de Evaluación
                  </div>
                  <div className="text-xs text-gray-500">Copia estructura de formularios (sin respuestas)</div>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.copiarTareas}
                  onChange={(e) => setFormData({ ...formData, copiarTareas: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                    Tareas y Trabajos
                  </div>
                  <div className="text-xs text-gray-500">Copia configuración de tareas</div>
                </div>
              </label>
            </div>
          </div>

          {/* Advertencia */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex gap-3">
              <i className="fas fa-info-circle text-yellow-600 mt-1"></i>
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Información importante:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>La nueva clase se creará como <strong>INACTIVA</strong></li>
                  <li>NO se copiarán: estudiantes, calificaciones ni asistencias</li>
                  <li>Podrás editar todos los detalles después de duplicar</li>
                  <li>Si hay algún error, podrás eliminarla mientras esté vacía</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Duplicando...
                </>
              ) : (
                <>
                  <i className="fas fa-copy"></i>
                  Duplicar Clase
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DuplicarClaseModal;
